// Field behavior copied from the storefront AuthRegistration components.

import React, { useEffect, useRef } from 'react'
import { AUTH_OTP_LENGTH } from '../authRegistrationConstants'
import styles from '../AuthRegistration.module.scss'

function toDigits(value) {
  return String(value || '')
    .replace(/\D/g, '')
    .slice(0, AUTH_OTP_LENGTH)
}

/** WebOTP is useful on Android Chrome; desktop calls have crashed tabs here. */
function canUseWebOtp() {
  if (typeof window === 'undefined') return false
  if (!window.isSecureContext) return false
  if (!('OTPCredential' in window)) return false
  if (typeof navigator.credentials?.get !== 'function') return false
  const ua = navigator.userAgent || ''
  const isAndroid = /Android/i.test(ua)
  const isChromeFamily = /Chrome|CriOS|Edg/i.test(ua) && !/OPR|Opera/i.test(ua)
  return isAndroid && isChromeFamily
}

export default function OtpCodeInput({ value, onChange, disabled, hasError, autoFocus }) {
  const inputsRef = useRef([])
  const onChangeRef = useRef(onChange)
  const digits = toDigits(value).padEnd(AUTH_OTP_LENGTH, ' ').slice(0, AUTH_OTP_LENGTH).split('')

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    if (!autoFocus) return undefined
    const id = window.requestAnimationFrame(() => {
      inputsRef.current[0]?.focus()
    })
    return () => window.cancelAnimationFrame(id)
  }, [autoFocus])

  // Android Chrome WebOTP only — gated + delayed to avoid tab crashes seen on desktop
  useEffect(() => {
    if (!canUseWebOtp()) return undefined

    let cancelled = false
    let ac
    const timer = window.setTimeout(() => {
      try {
        ac = new AbortController()
        navigator.credentials
          .get({
            otp: { transport: ['sms'] },
            signal: ac.signal,
          })
          .then((cred) => {
            if (cancelled) return
            const code = toDigits(cred?.code)
            if (!code) return
            onChangeRef.current(code)
            const focusIndex = Math.min(Math.max(code.length - 1, 0), AUTH_OTP_LENGTH - 1)
            inputsRef.current[focusIndex]?.focus()
          })
          .catch(() => {})
      } catch {
        // API present but call failed synchronously — ignore
      }
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
      try {
        ac?.abort()
      } catch {
        // ignore
      }
    }
  }, [])

  const commit = (next) => {
    onChange(toDigits(next))
  }

  const handleChange = (index, raw) => {
    const cleaned = String(raw || '').replace(/\D/g, '')
    if (!cleaned) {
      const arr = toDigits(value).split('')
      arr[index] = ''
      commit(arr.join(''))
      return
    }

    // Paste / SMS autofill may dump several digits into one cell
    if (cleaned.length > 1) {
      const merged = `${toDigits(value).slice(0, index)}${cleaned}`.slice(0, AUTH_OTP_LENGTH)
      commit(merged)
      const focusIndex = Math.min(Math.max(merged.length - 1, 0), AUTH_OTP_LENGTH - 1)
      inputsRef.current[focusIndex]?.focus()
      return
    }

    const arr = toDigits(value).padEnd(AUTH_OTP_LENGTH, '').split('').slice(0, AUTH_OTP_LENGTH)
    arr[index] = cleaned
    commit(arr.join(''))
    if (index < AUTH_OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      const current = toDigits(value)
      if (!current[index] && index > 0) {
        event.preventDefault()
        const arr = current.split('')
        arr[index - 1] = ''
        commit(arr.join(''))
        inputsRef.current[index - 1]?.focus()
      }
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      inputsRef.current[index - 1]?.focus()
    }
    if (event.key === 'ArrowRight' && index < AUTH_OTP_LENGTH - 1) {
      event.preventDefault()
      inputsRef.current[index + 1]?.focus()
    }
  }

  const handlePaste = (event) => {
    event.preventDefault()
    const pasted = toDigits(event.clipboardData.getData('text'))
    if (!pasted) return
    commit(pasted)
    const focusIndex = Math.min(Math.max(pasted.length - 1, 0), AUTH_OTP_LENGTH - 1)
    inputsRef.current[focusIndex]?.focus()
  }

  return (
    <div
      className={[styles.otpRow, hasError ? styles.otpRowError : ''].filter(Boolean).join(' ')}
      role="group"
      aria-label="Код із SMS"
      onPaste={handlePaste}
    >
      {Array.from({ length: AUTH_OTP_LENGTH }).map((_, index) => (
        <input
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          ref={(el) => {
            inputsRef.current[index] = el
          }}
          className={styles.otpCell}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          name={index === 0 ? 'one-time-code' : undefined}
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          // First cell accepts full code so iOS/Android autofill can land in one shot
          maxLength={index === 0 ? AUTH_OTP_LENGTH : 1}
          disabled={disabled}
          aria-label={`Цифра ${index + 1}`}
          value={digits[index] === ' ' ? '' : digits[index]}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
        />
      ))}
    </div>
  )
}
